import React from "react";

import TextPreset from "../../../Logos To be used/Text preset.svg";
import ImagePreset from "../../../Logos To be used/Image Preset Black.svg";
import VideoPreset from "../../../Logos To be used/Video Preset.svg";
import YoutubePreset from "../../../Logos To be used/Youtub vid preset.svg";

// Shared tutor helpers, defaults, icons, and small UI chrome.

export const getPositionValue = (item) => {
  const parsed = Number(item?.position);
  return Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER;
};

export const sortPos = (a, b) => {
  const diff = getPositionValue(a) - getPositionValue(b);
  if (diff !== 0) return diff;
  return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
};

export const maxPosPlusOne = (arr) => {
  const maxPos = (arr || []).reduce((m, x) => {
    const parsed = Number(x?.position);
    const pos = Number.isFinite(parsed) ? parsed : -1;
    return Math.max(m, pos);
  }, -1);
  return maxPos + 1;
};

export const getCreateSubjectBlockPositions = (arr) => {
  const zeroBasedPosition = maxPosPlusOne(arr);
  const oneBasedPosition = zeroBasedPosition <= 0 ? 1 : zeroBasedPosition;
  return Array.from(
    new Set(
      [zeroBasedPosition, oneBasedPosition].filter(
        (pos) => Number.isFinite(pos) && pos >= 0
      )
    )
  );
};

export const makeTempId5 = () =>
  `tmp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

export const DEFAULT_QUIZ_FORM5 = {
  question: "",
  questionType: "single_choice",
  isKlausurRelevant: false,
  requiredForCompletion: true,
  difficulty: "mittel",
  sourceType: "manual",
  explanation: "",
  hint: "",
};

export const DEFAULT_TASK_FORM5 = {
  title: "",
  taskText: "",
  hintText: "",
  answerText: "",
  difficulty: "mittel",
  estimatedMinutes: 15,
  isPublished: true,
};

export const DEFAULT_ALT_QUIZ_FORM5 = {
  questionText: "",
  questionType: "single_choice",
  points: 1,
  sourceType: "tutor",
  explanation: "",
  hint: "",
};

export const DEFAULT_ALTKLAUSUR_FORM5 = {
  title: "",
  description: "",
  isPublished: false,
};

export const normalizeQuizType5 = (value) => {
  if (value === "multiple_choice" || value === "single_choice") return value;
  if (value === "multiple" || value === "multi_select") return "multiple_choice";
  return "single_choice";
};

export const toLessonQuizQuestionType5 = (value) =>
  normalizeQuizType5(value) === "multiple_choice" ? "multi_select" : "single_choice";

export const normalizeDifficulty5 = (value) => {
  const lowered = String(value || "").toLowerCase();
  if (["easy", "einfach"].includes(lowered)) return "einfach";
  if (["hard", "schwer"].includes(lowered)) return "schwer";
  if (["medium", "mittel"].includes(lowered)) return "mittel";
  return lowered || "mittel";
};

export const normalizeAltklausurQuizSourceType5 = (value, fallback = "tutor") => {
  const safeFallback = ["professor", "tutor", "ai"].includes(fallback)
    ? fallback
    : "tutor";
  const normalized = String(value || "").trim().toLowerCase();

  if (["professor", "tutor", "ai"].includes(normalized)) return normalized;
  if (["manual", "official"].includes(normalized)) return safeFallback;
  return safeFallback;
};

export const buildQuizInput5 = (quiz, positionOverride) => ({
  id: quiz.id,
  question: quiz.question || "",
  questionType: toLessonQuizQuestionType5(quiz.questionType),
  position: Number.isFinite(Number(positionOverride))
    ? Number(positionOverride)
    : Number(quiz.position || 0),
  isKlausurRelevant: !!quiz.isKlausurRelevant,
  requiredForCompletion: quiz.requiredForCompletion !== false,
  difficulty: normalizeDifficulty5(quiz.difficulty),
  sourceType: quiz.sourceType || "manual",
  explanation: quiz.explanation || "",
  hint: quiz.hint || "",
});

export const buildTaskInput5 = (task, positionOverride) => ({
  id: task.id,
  title: task.title || "",
  taskText: task.taskText || "",
  hintText: task.hintText || "",
  answerText: task.answerText || "",
  position: Number.isFinite(Number(positionOverride))
    ? Number(positionOverride)
    : Number(task.position || 0),
  difficulty: normalizeDifficulty5(task.difficulty),
  estimatedMinutes: Math.max(0, Number(task.estimatedMinutes || 0)),
  isPublished: !!task.isPublished,
});

export const buildAltklausurTaskInput5 = (task, positionOverride) => ({
  id: task.id,
  title: task.title || "",
  taskText: task.taskText || "",
  hintText: task.hintText || "",
  answerText: task.answerText || "",
  position: Number.isFinite(Number(positionOverride))
    ? Number(positionOverride)
    : Number(task.position || 0),
  difficulty: normalizeDifficulty5(task.difficulty),
  estimatedMinutes: Math.max(0, Number(task.estimatedMinutes || 0)),
  isPublished: !!task.isPublished,
});

export const buildAltklausurQuestionInput5 = (question, positionOverride) => ({
  id: question.id,
  position: Number.isFinite(Number(positionOverride))
    ? Number(positionOverride)
    : Number(question.position || 0),
  questionText: question.questionText || "",
  questionType: normalizeQuizType5(question.questionType),
  explanation: question.explanation || "",
  hint: question.hint || "",
  points: Math.max(1, Number(question.points || 1)),
  sourceType: normalizeAltklausurQuizSourceType5(question.sourceType),
});

export const getPrimaryAltVersion5 = (alt) => (alt?.versions || [])[0] || null;

export const getAltContentCounts5 = (alt) => {
  const version = getPrimaryAltVersion5(alt);
  return {
    tasks: version?.tasks?.length || 0,
    quizzes: version?.quizQuestions?.length || 0,
  };
};

export const buildInternalAltVersionInput5 = (alt, existingVersion = null) => ({
  altklausurID: alt.id,
  title: alt.title || "Altklausur",
  versionDate: existingVersion?.versionDate || null,
  description: alt.description || "",
  sourceType: existingVersion?.sourceType || "manual",
  timeLimitSeconds: Math.max(0, Number(existingVersion?.timeLimitSeconds || 0)),
  isPublished: alt.isPublished !== false,
  isLocked: !!existingVersion?.isLocked,
});

export const DotsButton5 = ({ isOpen, onClick, visible }) => {
  return (
    <button
      type="button"
      className={`tutorR-dots5 ${isOpen ? "is-open5" : ""} ${visible ? "is-visible5" : ""}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      aria-label="Optionen"
    >
      <span />
      <span />
      <span />
    </button>
  );
};

export const TypeChooser5 = ({ open, onClose, placement = "belowLine", onPick }) => {
  if (!open) return null;

  return (
    <div
      className={`tutorR-typeChooser5 ${placement === "belowPlus" ? "belowPlus5" : ""}`}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="tutorR-typeBtn5"
        onClick={() => {
          onPick?.("text");
          onClose?.();
        }}
        aria-label="Text"
      >
        <img src={TextPreset} alt="Preset" draggable="false" />
        <span className="tutorR-typeHoverRing5" />
      </button>

      <button
        type="button"
        className="tutorR-typeBtn5"
        onClick={() => onClose?.()}
        aria-label="Bild"
        title="(später) Bild"
      >
        <img src={ImagePreset} alt="Preset" draggable="false" />
        <span className="tutorR-typeHoverRing5" />
      </button>

      <button
        type="button"
        className="tutorR-typeBtn5"
        onClick={() => onClose?.()}
        aria-label="Video"
        title="(später) Video"
      >
        <img src={VideoPreset} alt="Preset" draggable="false" />
        <span className="tutorR-typeHoverRing5" />
      </button>

      <button
        type="button"
        className="tutorR-typeBtn5"
        onClick={() => onClose?.()}
        aria-label="YouTube"
        title="(später) YouTube"
      >
        <img src={YoutubePreset} alt="Preset" draggable="false" />
        <span className="tutorR-typeHoverRing5" />
      </button>
    </div>
  );
};

/* ---------- chevrons/pencil ---------- */

export const ChevronUp5 = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 14l6-6 6 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const ChevronDown5 = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M6 10l6 6 6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Pencil5 = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M3 17.25V21h3.75L17.8 9.95l-3.75-3.75L3 17.25Z" stroke="currentColor" strokeWidth="2" />
    <path d="M14.06 4.94 19.06 9.94" stroke="currentColor" strokeWidth="2" />
  </svg>
);

export const Trash5 = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 7h14M9 7V5h6v2m-8 0 1 12h8l1-12"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M10 11v5m4-5v5" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

export const Menu5 = ({
  variant,
  canUp,
  canDown,
  onEdit,
  onUp,
  onDown,
  onDelete,
  aiEnabled,
  onToggleAI,
}) => {
  return (
    <div className="tutorR-menu5">
      <button type="button" className="tutorR-menuItem5" onClick={onEdit}>
        <span className="tutorR-menuIcon5">
          <Pencil5 className="tutorR-menuSvg5 tutorR-menuSvg-edit5" />
        </span>
        Bearbeiten
      </button>

      <button
        type="button"
        className={`tutorR-menuItem5 ${!canUp ? "disabled5" : ""}`}
        onClick={() => {
          if (!canUp) return;
          onUp?.();
        }}
      >
        <span className={`tutorR-menuIcon5 ${!canUp ? "disabled5" : ""}`}>
          <ChevronUp5 className="tutorR-menuSvg5 tutorR-menuSvg-up5" />
        </span>
        Up
      </button>

      <button
        type="button"
        className={`tutorR-menuItem5 ${!canDown ? "disabled5" : ""}`}
        onClick={() => {
          if (!canDown) return;
          onDown?.();
        }}
      >
        <span className={`tutorR-menuIcon5 ${!canDown ? "disabled5" : ""}`}>
          <ChevronDown5 className="tutorR-menuSvg5 tutorR-menuSvg-down5" />
        </span>
        Down
      </button>

      {(variant === "subject" || variant === "lesson" || variant === "quiz" || variant === "task") && (
        <>
          <button
            type="button"
            className="tutorR-menuItem5 tutorR-menuItemDelete5"
            onClick={onDelete}
          >
            <span className="tutorR-menuIcon5 tutorR-menuIconDelete5">
              <Trash5 className="tutorR-menuSvg5" />
            </span>
            Löschen
          </button>
        </>
      )}

      {variant === "lesson" && (
        <>
          <div className="tutorR-menuDivider5" />
          <div className="tutorR-menuRow5">
            <div className="tutorR-menuText5">AI Erklärung</div>
            <button
              type="button"
              className={`tutorR-toggle5 ${aiEnabled ? "on5" : ""}`}
              onClick={onToggleAI}
              aria-label="AI Erklärung umschalten"
            >
              <span className="tutorR-toggleKnob5" />
            </button>
          </div>
        </>
      )}
    </div>
  );
};
